/**
 * PieChart - Distribution visualization
 *
 * Usage:
 *   <PieChart
 *     title="Traffic Sources"
 *     data={{
 *       segments: [
 *         { label: 'Organic', value: 45 },
 *         { label: 'Direct', value: 30 },
 *         { label: 'Referral', value: 25 },
 *       ]
 *     }}
 *   />
 */

import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { BlockProps } from './types';
import { CHART_COLORS, getSeriesColor } from './colors';
import './blocks.css';

export interface PieChartData {
  segments: Array<{
    label: string;
    value: number;
    color?: string;
  }>;
}

export interface PieChartProps extends BlockProps<PieChartData> {
  /** Chart height in pixels */
  height?: number;
  /** Inner radius for donut (0 for pie) */
  innerRadius?: number;
  /** Show labels on segments */
  showLabels?: boolean;
  /** Show percentages */
  showPercentages?: boolean;
}

export function PieChart({
  data,
  title,
  config,
  height = 250,
  innerRadius = 0,
  showLabels = false,
  showPercentages = true,
  className,
}: PieChartProps) {
  const { segments } = data;

  if (!segments || segments.length === 0) {
    return (
      <div className={`block-chart ${className || ''}`}>
        {title && <div className="block-chart-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const safeTotal = total || 1; // Guard against division by zero

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const segment = payload[0];
    const percentage = ((segment.value / safeTotal) * 100).toFixed(1);

    return (
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '6px',
        padding: '8px 12px',
        boxShadow: 'var(--popover-shadow, 0 2px 4px rgba(0,0,0,0.1))',
      }}>
        <p style={{
          color: segment.payload.fill,
          fontWeight: 600,
          margin: 0,
          fontSize: '14px',
        }}>
          {segment.name}: {segment.value.toLocaleString()} ({percentage}%)
        </p>
      </div>
    );
  };

  const renderLabel = ({ name, percent }: any) => {
    if (showLabels) {
      return showPercentages ? `${name} (${(percent * 100).toFixed(0)}%)` : name;
    }
    return null;
  };

  return (
    <div className={`block-chart ${className || ''}`}>
      {title && <div className="block-chart-title">{title}</div>}

      <div className="block-chart-container">
        <ResponsiveContainer width="100%" height={height}>
          <RechartsPieChart>
            <Pie
              data={segments}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius="80%"
              paddingAngle={2}
              label={showLabels ? renderLabel : undefined}
              labelLine={showLabels}
            >
              {segments.map((segment, index) => (
                <Cell
                  key={index}
                  fill={segment.color || config?.colors?.[index] || getSeriesColor(index)}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value, entry: any) => (
                <span style={{ color: 'var(--color-text)', fontSize: '12px' }}>
                  {value}
                  {showPercentages && (
                    <span style={{ color: 'var(--color-text-muted)', marginLeft: '4px' }}>
                      ({((entry.payload.value / safeTotal) * 100).toFixed(0)}%)
                    </span>
                  )}
                </span>
              )}
            />
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default PieChart;
